/**
 * dcent-send-coin-demo.js — "보내고 받기" 앱 화면 목업의 타이핑 루프 (drop-07 레인 B).
 *
 * 원본: ~/work/dcent-design-drops/drop-07-20260814/src/components/SendCoinDemo.tsx L9-42.
 * 마크업(snippets/dcent-send-coin-demo.liquid)은 data 속성만 내고 동작은 전부 여기 있다
 * (assets/dcent-pdp-buy.js 선례 — 소비처에 JS 를 두지 않는다).
 *
 * ── 상태 머신 ──────────────────────────────────────────────────────────────
 *   step 0     = 빈 상태(0 POL / 0.00 USD)
 *   step 1~4   = "2450" 을 한 자씩 타이핑 (2 → 24 → 245 → 2450)
 *   step 5     = 완성 유지 후 0 으로 되돌림
 *   STEP_DELAYS[step] × speed 만큼 기다렸다가 다음 step 으로 간다. speed 는 배수라
 *   0.5 면 2배 빠르다(TapToSign). USD 는 수량 × RATE(0.45)를 en-US 표기로 함께 갱신한다.
 *
 * ── 재생 조건 ──────────────────────────────────────────────────────────────
 *   · IntersectionObserver(threshold 0.4) — 40% 이상 보일 때만 재생하고 벗어나면 그 자리에 멈춘다.
 *   · prefers-reduced-motion: reduce — 타이머를 아예 안 만들고 완성 상태(step 5)로 정지한다.
 *   서버가 찍는 첫 화면이 이미 완성 상태라 JS 가 없어도 의미 있는 정지 화면이 남는다. 모션이
 *   허용되면 초기화 시점(대개 화면 밖)에 step 0 으로 되돌려 두고, 보일 때부터 타이핑을 시작한다.
 *
 * ── 마크업 계약 ────────────────────────────────────────────────────────────
 *   [data-dcent-send-coin]            인스턴스 루트. IntersectionObserver 관측 대상
 *     └ data-dcent-sc-speed="0.5"     스텝 딜레이 배수(기본 1). 숫자가 아니면 1
 *   [data-dcent-sc-amount]            수량 자리 (예: "2,450")
 *   [data-dcent-sc-usd]               USD 환산 자리 (예: "1,102.50" — 뒤의 " USD" 는 마크업 고정)
 *   data-dcent-sc-ready="1"           초기화 완료 표식. 재호출해도 두 번 배선되지 않는다
 *
 * 금액 표기는 원본의 toLocaleString("en-US") 그대로다 — 앱 화면 재현이라 샵 통화·로케일과
 * 무관한 고정 영문 표기다(우리 상품 가격이 아니다).
 * typeof window 가드로 import 시 부수효과 0(vitest 안전).
 */

const DIGITS = "2450";
const RATE = 0.45; // POL → USD 환산 (2,450 × 0.45 = 1,102.50)
const STEP_DELAYS = [800, 240, 240, 240, 240, 2400];
const LAST_STEP = STEP_DELAYS.length - 1;

/** step → 화면에 찍을 { amount, usd } 문자열. 원본 L39-42 와 같은 산식. */
function valuesForStep(step) {
  const typed = Math.min(step, DIGITS.length);
  const value = typed === 0 ? 0 : Number(DIGITS.slice(0, typed));
  return {
    amount: value.toLocaleString("en-US"),
    usd: (value * RATE).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
  };
}

export function initSendCoinDemo(root = document) {
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  root.querySelectorAll("[data-dcent-send-coin]").forEach((el) => {
    // 같은 노드에 두 번 배선되면 타이머가 겹쳐 숫자가 튄다(테마 편집기 재로드·중복 초기화).
    if (el.dataset.dcentScReady === "1") return;
    el.dataset.dcentScReady = "1";

    const amountEl = el.querySelector("[data-dcent-sc-amount]");
    const usdEl = el.querySelector("[data-dcent-sc-usd]");
    if (!amountEl || !usdEl) return;

    const parsedSpeed = Number(el.dataset.dcentScSpeed);
    const speed =
      Number.isFinite(parsedSpeed) && parsedSpeed > 0 ? parsedSpeed : 1;

    const render = (step) => {
      const { amount, usd } = valuesForStep(step);
      amountEl.textContent = amount;
      usdEl.textContent = usd;
    };

    // 모션을 원치 않으면 완성 상태로 멈춘다(서버 렌더와 같은 값 — 관측자도 타이머도 만들지 않는다).
    if (reduce) {
      render(LAST_STEP);
      return;
    }

    let step = 0;
    let timer = 0;
    let playing = false;
    render(step);

    const schedule = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(
        () => {
          step = step >= LAST_STEP ? 0 : step + 1;
          render(step);
          schedule();
        },
        STEP_DELAYS[Math.min(step, LAST_STEP)] * speed,
      );
    };

    const setPlaying = (on) => {
      if (on === playing) return;
      playing = on;
      if (on) schedule();
      else window.clearTimeout(timer);
    };

    // 40% 이상 보일 때만 재생 — 벗어나면 그 step 에서 멈추고 다시 보이면 이어서 간다.
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => setPlaying(entry.isIntersecting));
      },
      { threshold: 0.4 },
    );
    io.observe(el);
  });
}

if (typeof window !== "undefined" && typeof document !== "undefined") {
  document.addEventListener("DOMContentLoaded", () => initSendCoinDemo());
  // 테마 편집기가 섹션 DOM 을 AJAX 로 갈아끼울 때 재초기화(라이브 스토어는 미발생).
  document.addEventListener("shopify:section:load", (e) =>
    initSendCoinDemo(e.target),
  );
}
